import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { getPushDomains, setupPushDomain, regeneratePushScripts, updatePushTriggerMode } from '@/services/api/push';
import { PushDomain } from '@/types/push';
import {
    Settings,
    CheckCircle2,
    Copy,
    ExternalLink,
    Loader2,
    Globe,
    Code2,
    Shield,
    ChevronDown,
    ChevronUp,
    Bell,
    Zap,
    FileCode,
    Download,
    Key,
    ArrowRight,
    Search,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    MousePointer,
    Radio,
} from 'lucide-react';

const PUSH_API_URL = import.meta.env.VITE_PUSH_API_URL || 'https://push-notification.rocketgroup.io';

const getFaviconUrl = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

export default function Setup() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [expandedDomain, setExpandedDomain] = useState<number | null>(null);
    const [faviconErrors, setFaviconErrors] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [purposeFilter, setPurposeFilter] = useState<'normal' | 'capture'>('normal');
    const perPage = 5;

    const handleFaviconError = useCallback((domain: string) => {
        setFaviconErrors(prev => new Set(prev).add(domain));
    }, []);

    const { data: domains = [], isLoading } = useQuery({
        queryKey: ['push-domains'],
        queryFn: getPushDomains,
    });

    const setupMutation = useMutation({
        mutationFn: setupPushDomain,
        onSuccess: () => {
            toast({ title: 'Domínio configurado!', description: 'Push notification ativado com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['push-domains'] });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message || 'Erro ao configurar domínio.' });
        },
    });

    const regenerateMutation = useMutation({
        mutationFn: regeneratePushScripts,
        onSuccess: () => {
            toast({ title: 'Scripts regenerados!', description: 'push.js e sw.js atualizados com sucesso.' });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message || 'Erro ao regenerar scripts.' });
        },
    });

    const triggerModeMutation = useMutation({
        mutationFn: ({ domainId, mode }: { domainId: number; mode: 'auto' | 'manual' }) =>
            updatePushTriggerMode(domainId, mode),
        onSuccess: (_, { mode }) => {
            toast({
                title: 'Modo de ativação alterado!',
                description: mode === 'auto'
                    ? 'Push será solicitado automaticamente na primeira visita.'
                    : 'Push será solicitado apenas via RequestPush().',
            });
            queryClient.invalidateQueries({ queryKey: ['push-domains'] });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message || 'Erro ao alterar modo.' });
        },
    });

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: 'Copiado!', description: `${label} copiado para a área de transferência.` });
    };

    const getScriptTag = (domainId: number) => {
        return `<script src="${PUSH_API_URL}/scripts/${domainId}/push.js" defer></script>`;
    };

    const getSwUrl = (domainId: number) => {
        return `${PUSH_API_URL}/scripts/${domainId}/sw.js`;
    };

    const isConfigured = (domain: PushDomain) => {
        return domain.push_config && domain.push_config.mongo_domain_id;
    };

    const domainsByPurpose = useMemo(() => {
        return domains.filter(d => (d.purpose || 'normal') === purposeFilter);
    }, [domains, purposeFilter]);

    const configuredCount = domainsByPurpose.filter(isConfigured).length;
    const totalCount = domainsByPurpose.length;
    const captureCount = domains.filter(d => d.purpose === 'capture').length;
    const normalCount = domains.filter(d => (d.purpose || 'normal') === 'normal').length;

    const filteredDomains = useMemo(() => {
        if (!search.trim()) return domainsByPurpose;
        const q = search.toLowerCase().trim();
        return domainsByPurpose.filter(d => d.domain.toLowerCase().includes(q));
    }, [domainsByPurpose, search]);

    const totalPages = Math.max(1, Math.ceil(filteredDomains.length / perPage));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedDomains = filteredDomains.slice((safePage - 1) * perPage, safePage * perPage);

    const handleSearch = (value: string) => {
        setSearch(value);
        setCurrentPage(1);
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-start gap-5">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-200">
                    <Bell className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">Setup Push Notification</h1>
                    <p className="mt-1 text-sm text-gray-500">Configure seus domínios para enviar push notifications</p>
                </div>
                {!isLoading && totalCount > 0 && (
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">{configuredCount}/{totalCount}</p>
                            <p className="text-xs text-gray-500">configurados</p>
                        </div>
                        <div className="h-10 w-10 relative">
                            <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                                <circle
                                    cx="18" cy="18" r="15.5" fill="none"
                                    stroke="#10b981" strokeWidth="3"
                                    strokeDasharray={`${(configuredCount / Math.max(totalCount, 1)) * 97.4} 97.4`}
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                    </div>
                )}
            </div>

            {/* Steps Guide */}
            <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-900">Como configurar</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { step: 1, icon: Zap, title: 'Ativar Push', desc: 'Clique em "Configurar" no domínio desejado' },
                        { step: 2, icon: FileCode, title: 'Copiar Script', desc: 'Cole o script no <head> do seu site' },
                        { step: 3, icon: Download, title: 'Service Worker', desc: 'Baixe o SW e coloque na raiz do site' },
                        { step: 4, icon: CheckCircle2, title: 'Pronto!', desc: 'Visitantes receberão o pedido de permissão' },
                    ].map(({ step, icon: Icon, title, desc }) => (
                        <div key={step} className="flex items-start gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-900">
                                    <span className="text-emerald-600">{step}.</span> {title}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white p-16">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                        <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
                    </div>
                    <p className="mt-4 text-sm text-gray-500">Carregando domínios...</p>
                </div>
            )}

            {/* Purpose Tabs */}
            {!isLoading && domains.length > 0 && (
                <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 w-fit">
                    <button
                        onClick={() => { setPurposeFilter('normal'); setCurrentPage(1); }}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            purposeFilter === 'normal'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Globe className="h-4 w-4" />
                        Dominios
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                            purposeFilter === 'normal' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                            {normalCount}
                        </span>
                    </button>
                    <button
                        onClick={() => { setPurposeFilter('capture'); setCurrentPage(1); }}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            purposeFilter === 'capture'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Radio className="h-4 w-4" />
                        Dominios de Captura
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                            purposeFilter === 'capture' ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                            {captureCount}
                        </span>
                    </button>
                </div>
            )}

            {/* Search */}
            {!isLoading && domainsByPurpose.length > 0 && (
                <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Pesquisar domínio..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10 rounded-xl border-gray-200 h-11 text-sm bg-white focus:border-emerald-300 focus:ring-emerald-200"
                    />
                    {search && (
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            {filteredDomains.length} resultado{filteredDomains.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            )}

            {/* No domains for selected purpose */}
            {!isLoading && domains.length > 0 && domainsByPurpose.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-12">
                    <Radio className="h-8 w-8 text-gray-300" />
                    <h3 className="mt-4 text-sm font-semibold text-gray-900">
                        {purposeFilter === 'capture' ? 'Nenhum dominio de captura' : 'Nenhum dominio normal'}
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                        {purposeFilter === 'capture'
                            ? 'Crie dominios de captura na pagina de Dominios para usa-los aqui.'
                            : 'Adicione dominios normais para configurar push notifications.'}
                    </p>
                </div>
            )}

            {/* Domain List */}
            {!isLoading && domainsByPurpose.length > 0 && (
                <div className="space-y-4">
                    {paginatedDomains.map((domain) => {
                        const configured = isConfigured(domain);
                        const isExpanded = expandedDomain === domain.id;

                        return (
                            <div
                                key={domain.id}
                                className={`overflow-hidden rounded-2xl border transition-all duration-200 ${
                                    configured
                                        ? isExpanded
                                            ? 'border-emerald-200 shadow-lg shadow-emerald-50'
                                            : 'border-gray-100 bg-white shadow-sm hover:shadow-md hover:border-emerald-100'
                                        : 'border-gray-100 bg-white shadow-sm hover:shadow-md'
                                }`}
                            >
                                {/* Domain Header */}
                                <div className="flex items-center justify-between p-5">
                                    <div className="flex items-center gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-xl overflow-hidden ${
                                            configured
                                                ? 'bg-gradient-to-br from-emerald-50 to-emerald-100'
                                                : 'bg-gray-50'
                                        }`}>
                                            {!faviconErrors.has(domain.domain) ? (
                                                <img
                                                    src={getFaviconUrl(domain.domain)}
                                                    alt={domain.domain}
                                                    className="h-8 w-8 rounded-lg object-contain"
                                                    onError={() => handleFaviconError(domain.domain)}
                                                />
                                            ) : (
                                                <Globe className={`h-6 w-6 ${configured ? 'text-emerald-600' : 'text-gray-400'}`} />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{domain.domain}</h3>
                                            <div className="mt-1 flex items-center gap-2">
                                                {configured ? (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                        Push ativo
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                                        Não configurado
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {configured ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setExpandedDomain(isExpanded ? null : domain.id)}
                                                className={`gap-2 transition-colors ${
                                                    isExpanded
                                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                                        : 'hover:border-emerald-200 hover:text-emerald-700'
                                                }`}
                                            >
                                                <Code2 className="h-4 w-4" />
                                                {isExpanded ? 'Ocultar Scripts' : 'Ver Scripts'}
                                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                onClick={() => setupMutation.mutate(domain.id)}
                                                disabled={setupMutation.isPending}
                                                className="gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-sm"
                                            >
                                                {setupMutation.isPending ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Settings className="h-4 w-4" />
                                                )}
                                                Configurar
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Scripts Section */}
                                {isExpanded && configured && (
                                    <div className="border-t border-emerald-100 bg-gradient-to-b from-gray-50/80 to-white">
                                        <div className="p-5 space-y-5">
                                            {/* Script Tag */}
                                            <div className="rounded-xl border border-gray-100 bg-white p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50">
                                                            <FileCode className="h-4 w-4 text-emerald-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-gray-900">Script Tag</p>
                                                            <p className="text-xs text-gray-500">Cole no {'<head>'} do seu site</p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => copyToClipboard(getScriptTag(domain.id), 'Script tag')}
                                                        className="gap-1.5 text-xs hover:border-emerald-200 hover:text-emerald-700"
                                                    >
                                                        <Copy className="h-3.5 w-3.5" /> Copiar
                                                    </Button>
                                                </div>
                                                <div className="relative overflow-hidden rounded-lg bg-gray-900 p-4">
                                                    <pre className="overflow-x-auto text-sm leading-relaxed text-emerald-400 font-mono">
                                                        {getScriptTag(domain.id)}
                                                    </pre>
                                                </div>
                                            </div>

                                            {/* Service Worker */}
                                            <div className="rounded-xl border border-gray-100 bg-white p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
                                                            <Download className="h-4 w-4 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-gray-900">Service Worker</p>
                                                            <p className="text-xs text-gray-500">Baixe e coloque na raiz como /sw.js</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(getSwUrl(domain.id), 'URL do SW')}
                                                            className="gap-1.5 text-xs hover:border-emerald-200 hover:text-emerald-700"
                                                        >
                                                            <Copy className="h-3.5 w-3.5" /> URL
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => window.open(getSwUrl(domain.id), '_blank')}
                                                            className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700"
                                                        >
                                                            <ExternalLink className="h-3.5 w-3.5" /> Baixar
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="relative overflow-hidden rounded-lg bg-gray-900 p-4">
                                                    <pre className="overflow-x-auto text-sm leading-relaxed text-blue-400 font-mono">
                                                        {getSwUrl(domain.id)}
                                                    </pre>
                                                </div>
                                            </div>

                                            {/* VAPID Key */}
                                            {domain.push_config?.vapid_public_key && (
                                                <div className="rounded-xl border border-gray-100 bg-white p-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-50">
                                                                <Key className="h-4 w-4 text-amber-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900">VAPID Public Key</p>
                                                                <p className="text-xs text-gray-500">Chave pública de identificação</p>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(domain.push_config!.vapid_public_key!, 'VAPID Key')}
                                                            className="gap-1.5 text-xs hover:border-emerald-200 hover:text-emerald-700"
                                                        >
                                                            <Copy className="h-3.5 w-3.5" /> Copiar
                                                        </Button>
                                                    </div>
                                                    <div className="relative overflow-hidden rounded-lg bg-gray-900 p-4">
                                                        <pre className="overflow-x-auto text-xs leading-relaxed text-amber-400 font-mono break-all whitespace-pre-wrap">
                                                            {domain.push_config.vapid_public_key}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Trigger Mode */}
                                            <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50">
                                                        <Radio className="h-4 w-4 text-purple-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-900">Modo de Ativação</p>
                                                        <p className="text-xs text-gray-500">Quando o push será solicitado ao visitante</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => triggerModeMutation.mutate({ domainId: domain.id, mode: 'auto' })}
                                                        disabled={triggerModeMutation.isPending}
                                                        className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                                                            (domain.push_config?.trigger_mode || 'auto') === 'auto'
                                                                ? 'border-purple-500 bg-purple-50/50 shadow-sm'
                                                                : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/30'
                                                        }`}
                                                    >
                                                        {(domain.push_config?.trigger_mode || 'auto') === 'auto' && (
                                                            <div className="absolute right-2 top-2">
                                                                <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                                            </div>
                                                        )}
                                                        <Zap className="h-5 w-5 text-purple-600 mb-2" />
                                                        <p className="text-sm font-semibold text-gray-900">Primeira interação</p>
                                                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                                                            Solicita permissão automaticamente quando o visitante acessa o site
                                                        </p>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => triggerModeMutation.mutate({ domainId: domain.id, mode: 'manual' })}
                                                        disabled={triggerModeMutation.isPending}
                                                        className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                                                            domain.push_config?.trigger_mode === 'manual'
                                                                ? 'border-purple-500 bg-purple-50/50 shadow-sm'
                                                                : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/30'
                                                        }`}
                                                    >
                                                        {domain.push_config?.trigger_mode === 'manual' && (
                                                            <div className="absolute right-2 top-2">
                                                                <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                                            </div>
                                                        )}
                                                        <MousePointer className="h-5 w-5 text-purple-600 mb-2" />
                                                        <p className="text-sm font-semibold text-gray-900">Via Script</p>
                                                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                                                            Chame <code className="bg-gray-100 px-1 rounded text-[10px] font-mono">RequestPush()</code> quando quiser solicitar
                                                        </p>
                                                    </button>
                                                </div>
                                                {domain.push_config?.trigger_mode === 'manual' && (
                                                    <div className="rounded-lg bg-gray-900 p-3">
                                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Exemplo de uso</p>
                                                        <pre className="text-xs text-emerald-400 font-mono leading-relaxed overflow-x-auto">{`<button onclick="RequestPush()">
  Ativar Notificações
</button>`}</pre>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Regenerar Scripts */}
                                            <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
                                                        <RefreshCw className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-900">Regenerar Scripts</p>
                                                        <p className="text-xs text-gray-500">Atualiza push.js e sw.js com a versão mais recente</p>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => regenerateMutation.mutate(domain.id)}
                                                    disabled={regenerateMutation.isPending}
                                                    className="gap-1.5 text-xs hover:border-blue-200 hover:text-blue-700"
                                                >
                                                    {regenerateMutation.isPending ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="h-3.5 w-3.5" />
                                                    )}
                                                    Regenerar
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Pagination */}
                    {filteredDomains.length > perPage && (
                        <div className="flex items-center justify-between pt-2">
                            <p className="text-xs text-gray-500">
                                Mostrando {(safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, filteredDomains.length)} de {filteredDomains.length} domínio{filteredDomains.length !== 1 ? 's' : ''}
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={safePage <= 1}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                                            safePage === page
                                                ? 'bg-emerald-600 text-white shadow-sm'
                                                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={safePage >= totalPages}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* No results from search */}
            {!isLoading && domainsByPurpose.length > 0 && filteredDomains.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-12">
                    <Search className="h-8 w-8 text-gray-300" />
                    <h3 className="mt-4 text-sm font-semibold text-gray-900">Nenhum domínio encontrado</h3>
                    <p className="mt-1 text-xs text-gray-500">Tente buscar por outro termo</p>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && domains.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-16">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50">
                        <Globe className="h-8 w-8 text-gray-300" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-gray-900">Nenhum domínio encontrado</h3>
                    <p className="mt-1.5 text-sm text-gray-500 text-center max-w-sm">
                        Adicione um domínio primeiro para configurar push notifications no seu site.
                    </p>
                </div>
            )}
        </div>
    );
}
